import { useStore } from '@nanostores/react';
import { useEffect, useMemo, useState } from 'react';
import { FieldsForm, draftErrors, type ReferenceLoader } from '~/components/workbench/fields';
import { createReferenceLoader } from '~/components/workbench/tools/content/references';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  LoadError,
  Pill,
  Section,
  Select,
  SkeletonCard,
  Spinner,
  Switch,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { apiBlockLibrary, apiGetLayout, apiSaveLayout } from '~/lib/api/blocks';
import { chatId } from '~/lib/persistence/useChatHistory';
import {
  PAGE_TYPES,
  type BlockInstance,
  type LayoutIssue,
  type LayoutOp,
  type LibraryBlock,
  type PageType,
  type SectionsFile,
} from '~/lib/plugin-sdk-layout';
import { workbenchStore } from '~/lib/stores/workbench';
import { classNames } from '~/utils/classNames';
import {
  PAGE_TYPE_LABELS,
  SOURCE_LABELS,
  addableChildren,
  addableSections,
  classifyError,
  edit,
  embedRows,
  formValues,
  groupIdFromName,
  groupIds,
  groupLabel,
  libraryIndex,
  sectionRows,
  settingFields,
  settingsToSave,
  stepOp,
  type SaveProblem,
  type SectionRow,
} from './sections-model';

/**
 * Owner-facing Sections panel (Blocks plugin): the sections of each page of
 * the site — reorder, hide, edit settings, add from the block library (the
 * app's own blocks, the theme's and plugins'), child blocks and app embeds.
 * Every change is saved to the app's src/content/sections.json and the
 * preview reloads; it goes live with the next publish.
 */
export function SectionsPanel() {
  const appId = useStore(chatId);

  return (
    <div className="relative flex h-full flex-col">
      <div className="border-b border-hairline px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/10 ring-1 ring-inset ring-accent/15">
            <div className="i-ph:stack text-[13px] text-accent" />
          </div>
          <h3 className="text-[13px] font-medium tracking-tight text-primary">Sections</h3>
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-muted">
          The building blocks of each page. Reorder, hide and edit them here; changes show in the preview and go live
          when you publish.
        </p>
      </div>

      {appId ? (
        <SectionsPanelBody key={appId} appId={appId} />
      ) : (
        <div className="flex flex-1 items-center justify-center px-6 text-center text-[12px] text-muted">
          Start building to edit your sections.
        </div>
      )}
    </div>
  );
}

function reloadPreview() {
  workbenchStore.previewReloadNonce.set(workbenchStore.previewReloadNonce.get() + 1);
}

function SectionsPanelBody({ appId }: { appId: string }) {
  const [tick, setTick] = useState(0);
  // Library first: opening the panel also applies pending plugin syncs, which
  // may touch sections.json, so the layout (and its version) is read after.
  const loaded = useResource(async () => {
    const library = await apiBlockLibrary(appId);
    const layout = await apiGetLayout(appId);

    return { library, layout };
  }, `${appId}:${tick}`);

  const [doc, setDoc] = useState<SectionsFile | null>(null);
  const [version, setVersion] = useState<string>('none');
  const [issues, setIssues] = useState<LayoutIssue[]>([]);
  const [group, setGroup] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<SaveProblem | null>(null);
  const [adding, setAdding] = useState(false);
  const [newGroup, setNewGroup] = useState(false);

  useEffect(() => {
    if (!loaded.data) {
      return;
    }

    const { layout } = loaded.data;
    setDoc(layout.document);
    setVersion(layout.version);
    setIssues(layout.valid ? [] : layout.errors);
    setProblem(null);

    const ids = groupIds(layout.document);
    setGroup((g) => (g && ids.includes(g) ? g : (ids[0] ?? null)));
  }, [loaded.data]);

  const blocks = loaded.data?.library.blocks ?? [];
  const index = useMemo(() => libraryIndex(blocks), [blocks]);
  const references = useMemo<ReferenceLoader>(() => createReferenceLoader(appId, () => []), [appId]);

  const commit = async (next: SectionsFile) => {
    setSaving(true);
    setProblem(null);

    try {
      const view = await apiSaveLayout(appId, next, version);
      setDoc(view.document);
      setVersion(view.version);
      setIssues([]);
      reloadPreview();

      return true;
    } catch (err) {
      const p = classifyError(err, 'Could not save your sections.');
      setProblem(p);

      if (p.kind === 'invalid') {
        setIssues(p.issues);
      }

      return false;
    } finally {
      setSaving(false);
    }
  };

  const apply = async (ops: LayoutOp[]) => {
    try {
      return await commit(edit(doc, ops, blocks));
    } catch (err) {
      setProblem({ kind: 'other', message: (err as Error).message });
      return false;
    }
  };

  if (loaded.loading && !loaded.data) {
    return (
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        <SkeletonCard lines={2} />
        <SkeletonCard lines={3} />
      </div>
    );
  }

  if (loaded.error) {
    const p = classifyError(loaded.error, 'Could not load your sections.');

    return (
      <div className="flex-1 overflow-y-auto p-4">
        {p.kind === 'asleep' ? (
          <AsleepCard onRetry={() => setTick((t) => t + 1)} />
        ) : (
          <LoadError
            message={p.kind === 'other' || p.kind === 'invalid' ? p.message : 'Could not load your sections.'}
            onRetry={() => setTick((t) => t + 1)}
          />
        )}
      </div>
    );
  }

  if (!loaded.data || !doc) {
    return null;
  }

  const { library } = loaded.data;
  const groups = groupIds(doc);
  const current = group && doc.groups[group] ? group : null;
  const pageType = (current ? doc.groups[current].pageType : 'any') as PageType;
  const rows = current ? sectionRows(doc, current, index, issues) : [];
  const embeds = embedRows(doc, blocks);
  const busy = saving;

  return (
    <div className="flex-1 space-y-6 overflow-y-auto p-4">
      {!library.runtime && (
        <div className="ui-banner-warning items-start leading-relaxed">
          <div className="i-ph:info mt-px shrink-0 text-sm text-warning" />
          <span className="text-[12px] text-secondary">
            This app was built before sections existed. Ask the AI to "turn my home page into sections" and it will set
            them up.
          </span>
        </div>
      )}

      {'error' in library.sync && <ErrorBanner message={library.sync.error} />}

      {problem && (
        <ProblemBanner
          problem={problem}
          onReload={() => {
            setProblem(null);
            setTick((t) => t + 1);
          }}
        />
      )}

      {issues.length > 0 && !problem && (
        <div className="ui-banner-warning items-start leading-relaxed">
          <div className="i-ph:warning mt-px shrink-0 text-sm text-warning" />
          <span className="text-[12px] text-secondary">
            Some sections need attention:{' '}
            {issues
              .slice(0, 3)
              .map((i) => i.message)
              .join('; ')}
            {issues.length > 3 ? '…' : ''}
          </span>
        </div>
      )}

      <Section
        title="Page"
        icon="i-ph:browser"
        aside={
          <button type="button" onClick={() => setNewGroup((v) => !v)} disabled={busy} className={BTN_GHOST}>
            <div className="i-ph:plus text-[13px]" />
            New page
          </button>
        }
      >
        {groups.length > 0 ? (
          <Select
            value={current ?? ''}
            onChange={(g) => {
              setGroup(g);
              setAdding(false);
            }}
            label="Page"
          >
            {groups.map((g) => (
              <option key={g} value={g}>
                {groupLabel(g)} · {PAGE_TYPE_LABELS[(doc.groups[g].pageType ?? 'any') as PageType]}
              </option>
            ))}
          </Select>
        ) : (
          !newGroup && (
            <div className={classNames(CARD, 'space-y-2 px-4 py-6 text-center')}>
              <div className="i-ph:stack mx-auto text-xl text-faint" />
              <p className="text-[12px] leading-relaxed text-muted">
                No sections yet. Ask the AI to build a page from sections, or start one here.
              </p>
              <button type="button" onClick={() => setNewGroup(true)} className={classNames(BTN_SECONDARY, 'mx-auto')}>
                <div className="i-ph:plus text-[13px]" />
                Start a page
              </button>
            </div>
          )
        )}

        {newGroup && (
          <NewGroupForm
            taken={groups}
            busy={busy}
            onCancel={() => setNewGroup(false)}
            onCreate={async (id, type) => {
              if (await apply([{ op: 'set_group', group: id, pageType: type }])) {
                setGroup(id);
                setNewGroup(false);
                setAdding(true);
              }
            }}
          />
        )}
      </Section>

      {current && (
        <Section
          title="Sections"
          icon="i-ph:rows"
          aside={
            <button type="button" onClick={() => setAdding((v) => !v)} disabled={busy} className={BTN_SECONDARY}>
              <div className={classNames(adding ? 'i-ph:x' : 'i-ph:plus', 'text-[13px]')} />
              {adding ? 'Close' : 'Add section'}
            </button>
          }
        >
          {adding && (
            <AddSectionPicker
              options={addableSections(blocks, pageType)}
              busy={busy}
              onAdd={async (type, preset) => {
                if (await apply([{ op: 'add_section', group: current, type, preset }])) {
                  setAdding(false);
                }
              }}
            />
          )}

          {rows.length === 0 && !adding ? (
            <div className={classNames(CARD, 'px-4 py-6 text-center text-[12px] text-muted')}>
              This page has no sections yet. Add one to get started.
            </div>
          ) : (
            rows.map((row) => (
              <SectionCard
                key={row.instance.id}
                row={row}
                siblings={doc.groups[current].sections}
                library={blocks}
                index={index}
                busy={busy}
                references={references}
                onOps={apply}
              />
            ))
          )}

          {groups.length > 0 && rows.length > 0 && (
            <p className="text-[11px] leading-relaxed text-muted">
              Shown on the site where a page renders{' '}
              <code className="font-geist-mono">{`<SectionGroup id="${current}" />`}</code>.
            </p>
          )}
        </Section>
      )}

      {embeds.length > 0 && (
        <Section title="App embeds" icon="i-ph:app-window">
          {embeds.map((e) => (
            <EmbedCard key={e.block.type} row={e} busy={busy} references={references} onOps={apply} />
          ))}
        </Section>
      )}

      {library.problems.length > 0 && (
        <details className="text-[11px] text-muted">
          <summary className="cursor-pointer select-none">
            {library.problems.length} block file{library.problems.length === 1 ? '' : 's'} could not be read
          </summary>
          <ul className="mt-2 space-y-1">
            {library.problems.map((p) => (
              <li key={p.path}>
                <span className="font-geist-mono">{p.path}</span> {p.message}
              </li>
            ))}
          </ul>
        </details>
      )}

      {busy && (
        <div className="flex items-center gap-2 text-[11px] text-muted" role="status">
          <Spinner />
          Saving…
        </div>
      )}
    </div>
  );
}

function AsleepCard({ onRetry }: { onRetry: () => void }) {
  return (
    <div className={classNames(CARD, 'space-y-3 px-4 py-6 text-center')}>
      <div className="i-ph:moon mx-auto text-xl text-faint" />
      <p className="text-[12px] leading-relaxed text-muted">
        Your app's preview is asleep. Open the preview to wake it, then come back to edit sections.
      </p>
      <div className="flex items-center justify-center gap-2">
        <button type="button" onClick={() => workbenchStore.setActiveTab('preview')} className={BTN_SECONDARY}>
          <div className="i-ph:globe-simple text-[13px]" />
          Open preview
        </button>
        <button type="button" onClick={onRetry} className={BTN_GHOST}>
          <div className="i-ph:arrow-clockwise text-[13px]" />
          Retry
        </button>
      </div>
    </div>
  );
}

function ProblemBanner({ problem, onReload }: { problem: SaveProblem; onReload: () => void }) {
  if (problem.kind === 'conflict') {
    return (
      <div className={classNames(CARD, 'flex items-center justify-between gap-3 px-4 py-3')}>
        <span className="text-[12px] leading-relaxed text-secondary">
          These sections were changed elsewhere (by the AI or another tab). Reload to see the latest, then try again.
        </span>
        <button type="button" onClick={onReload} className={BTN_SECONDARY}>
          <div className="i-ph:arrow-clockwise text-[13px]" />
          Reload
        </button>
      </div>
    );
  }

  if (problem.kind === 'asleep') {
    return <AsleepCard onRetry={onReload} />;
  }

  return <ErrorBanner message={problem.message} />;
}

function NewGroupForm({
  taken,
  busy,
  onCancel,
  onCreate,
}: {
  taken: string[];
  busy: boolean;
  onCancel: () => void;
  onCreate: (id: string, pageType: PageType) => void;
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState<PageType>(taken.length ? 'page' : 'home');
  const id = groupIdFromName(name || (taken.length ? '' : 'home'), taken);

  return (
    <form
      className={classNames(CARD, 'space-y-3 p-4')}
      onSubmit={(e) => {
        e.preventDefault();

        if (id) {
          onCreate(id, type);
        }
      }}
    >
      <label className="block space-y-1.5">
        <span className="block text-[11px] text-secondary">Page name</span>
        <input
          className="ui-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={taken.length ? 'About' : 'Home'}
          maxLength={60}
          autoFocus
        />
        {id && (
          <span className="block text-[11px] text-muted">
            Section group id: <code className="font-geist-mono">{id}</code>
          </span>
        )}
      </label>
      <label className="block space-y-1.5">
        <span className="block text-[11px] text-secondary">Kind of page</span>
        <Select value={type} onChange={(v) => setType(v as PageType)} label="Kind of page">
          {PAGE_TYPES.map((p) => (
            <option key={p} value={p}>
              {PAGE_TYPE_LABELS[p]}
            </option>
          ))}
        </Select>
      </label>
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy || !id} className={BTN_SECONDARY}>
          Create
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function AddSectionPicker({
  options,
  busy,
  onAdd,
}: {
  options: LibraryBlock[];
  busy: boolean;
  onAdd: (type: string, preset?: string) => void;
}) {
  if (options.length === 0) {
    return (
      <div className={classNames(CARD, 'px-4 py-5 text-center text-[12px] leading-relaxed text-muted')}>
        No sections fit this page yet. Ask the AI to build one ("add a testimonials section"), apply a theme with
        sections, or install a plugin from the Marketplace.
      </div>
    );
  }

  return (
    <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
      {options.map((b) => (
        <div key={b.type} className="space-y-2 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[12px] font-medium text-primary">{b.schema.title}</div>
              {b.schema.description && (
                <p className="mt-0.5 text-[11px] leading-relaxed text-muted">{b.schema.description}</p>
              )}
            </div>
            {b.source !== 'local' && <Pill tone="muted" label={SOURCE_LABELS[b.source]} />}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {b.schema.presets.length > 0 ? (
              b.schema.presets.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  disabled={busy}
                  onClick={() => onAdd(b.type, p.name)}
                  className={BTN_SECONDARY}
                >
                  <div className="i-ph:plus text-[12px]" />
                  {p.name}
                </button>
              ))
            ) : (
              <button type="button" disabled={busy} onClick={() => onAdd(b.type)} className={BTN_SECONDARY}>
                <div className="i-ph:plus text-[12px]" />
                Add
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function SectionCard({
  row,
  siblings,
  library,
  index,
  busy,
  references,
  onOps,
}: {
  row: SectionRow;
  siblings: BlockInstance[];
  library: LibraryBlock[];
  index: ReadonlyMap<string, LibraryBlock>;
  busy: boolean;
  references: ReferenceLoader;
  onOps: (ops: LayoutOp[]) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { instance, block } = row;
  const up = stepOp(siblings, instance.id, -1);
  const down = stepOp(siblings, instance.id, 1);

  return (
    <article className={classNames(CARD, 'overflow-hidden', row.hidden && 'opacity-70')}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={open}
        >
          <div
            className={classNames(open ? 'i-ph:caret-down' : 'i-ph:caret-right', 'shrink-0 text-[12px] text-muted')}
          />
          <div className="min-w-0">
            <div className="truncate text-[12px] font-medium text-primary">{row.title}</div>
            <div className="truncate font-geist-mono text-[10px] text-faint">{instance.type}</div>
          </div>
        </button>
        {!block && <Pill tone="warning" label="Missing" />}
        {row.issues.length > 0 && <Pill tone="danger" label="Fix" />}
        {row.hidden && <Pill tone="muted" label="Hidden" />}
        {block && block.source !== 'local' && <Pill tone="muted" label={SOURCE_LABELS[block.source]} />}
        <div className="flex shrink-0 items-center">
          <IconButton
            icon="i-ph:arrow-up"
            label="Move up"
            disabled={busy || !up}
            onClick={() => up && void onOps([up])}
          />
          <IconButton
            icon="i-ph:arrow-down"
            label="Move down"
            disabled={busy || !down}
            onClick={() => down && void onOps([down])}
          />
          <IconButton
            icon={row.hidden ? 'i-ph:eye-slash' : 'i-ph:eye'}
            label={row.hidden ? 'Show section' : 'Hide section'}
            disabled={busy}
            onClick={() => void onOps([{ op: 'update', id: instance.id, hidden: !row.hidden }])}
          />
        </div>
      </div>

      {open && (
        <div className="space-y-4 border-t border-hairline px-4 py-4">
          {!block ? (
            <p className="text-[12px] leading-relaxed text-muted">
              There is no <code className="font-geist-mono">{instance.type}</code> block in this app any more, so this
              section shows nothing. Remove it, or ask the AI to rebuild it.
            </p>
          ) : (
            <SettingsEditor
              block={block}
              settings={instance.settings}
              issues={row.issues}
              busy={busy}
              references={references}
              onSave={(settings) => onOps([{ op: 'update', id: instance.id, settings, replaceSettings: true }])}
            />
          )}

          {row.acceptsBlocks && block && (
            <ChildBlocks
              section={instance}
              sectionBlock={block}
              library={library}
              index={index}
              busy={busy}
              references={references}
              onOps={onOps}
            />
          )}

          <div className="flex items-center gap-2 border-t border-hairline pt-3">
            {confirming ? (
              <>
                <span className="text-[12px] text-secondary">Remove this section?</span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onOps([{ op: 'remove', id: instance.id }])}
                  className={classNames(BTN_SECONDARY, 'text-danger')}
                >
                  Remove
                </button>
                <button type="button" onClick={() => setConfirming(false)} className={BTN_GHOST}>
                  Keep
                </button>
              </>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirming(true)}
                className={classNames(BTN_GHOST, 'ml-auto')}
              >
                <div className="i-ph:trash text-[13px]" />
                Remove section
              </button>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

function IconButton({
  icon,
  label,
  disabled,
  onClick,
}: {
  icon: string;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="ui-btn-icon disabled:opacity-30"
      title={label}
      aria-label={label}
    >
      <div className={classNames(icon, 'text-[13px] text-muted')} />
    </button>
  );
}

function SettingsEditor({
  block,
  settings,
  issues,
  busy,
  references,
  onSave,
}: {
  block: LibraryBlock;
  settings: Record<string, unknown>;
  issues: LayoutIssue[];
  busy: boolean;
  references: ReferenceLoader;
  onSave: (settings: Record<string, unknown>) => Promise<boolean>;
}) {
  const fields = settingFields(block);
  const initial = useMemo(() => formValues(block, settings), [block, settings]);
  const [values, setValues] = useState(initial);

  useEffect(() => setValues(initial), [initial]);

  if (fields.length === 0) {
    return <p className="text-[12px] text-muted">This section has no settings.</p>;
  }

  const local = draftErrors(fields, values);
  const server = Object.fromEntries(
    issues.flatMap((i) => {
      const key = /\.settings\.([a-z][a-z0-9_]*)$/.exec(i.path)?.[1];
      return key ? [[key, i.message]] : [];
    }),
  );
  const errors = { ...server, ...local };
  const dirty = JSON.stringify(values) !== JSON.stringify(initial);

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();

        if (Object.keys(local).length === 0) {
          void onSave(settingsToSave(values));
        }
      }}
    >
      <FieldsForm
        fields={fields}
        values={values}
        onChange={setValues}
        errors={errors}
        disabled={busy}
        references={references}
      />
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy || !dirty || Object.keys(local).length > 0} className={BTN_SECONDARY}>
          Save
        </button>
        {dirty && (
          <button type="button" onClick={() => setValues(initial)} disabled={busy} className={BTN_GHOST}>
            Discard
          </button>
        )}
      </div>
    </form>
  );
}

function ChildBlocks({
  section,
  sectionBlock,
  library,
  index,
  busy,
  references,
  onOps,
}: {
  section: BlockInstance;
  sectionBlock: LibraryBlock;
  library: LibraryBlock[];
  index: ReadonlyMap<string, LibraryBlock>;
  busy: boolean;
  references: ReferenceLoader;
  onOps: (ops: LayoutOp[]) => Promise<boolean>;
}) {
  const children = section.blocks ?? [];
  const options = addableChildren(sectionBlock, library);
  const [open, setOpen] = useState<string | null>(null);
  const [pick, setPick] = useState('');
  const full = Boolean(sectionBlock.schema.maxBlocks && children.length >= sectionBlock.schema.maxBlocks);

  return (
    <div className="space-y-2">
      <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted">Blocks in this section</div>
      {children.length === 0 && <p className="text-[12px] text-muted">None yet.</p>}
      {children.map((child) => {
        const block = index.get(child.type) ?? null;
        const up = stepOp(children, child.id, -1);
        const down = stepOp(children, child.id, 1);

        return (
          <div key={child.id} className="rounded-lg border border-hairline bg-sunken">
            <div className="flex items-center gap-2 px-3 py-2">
              <button
                type="button"
                onClick={() => setOpen((v) => (v === child.id ? null : child.id))}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
              >
                <div
                  className={classNames(
                    open === child.id ? 'i-ph:caret-down' : 'i-ph:caret-right',
                    'shrink-0 text-[12px] text-muted',
                  )}
                />
                <span className="truncate text-[12px] text-secondary">{block?.schema.title ?? child.type}</span>
              </button>
              {child.hidden && <Pill tone="muted" label="Hidden" />}
              <IconButton
                icon="i-ph:arrow-up"
                label="Move up"
                disabled={busy || !up}
                onClick={() => up && void onOps([up])}
              />
              <IconButton
                icon="i-ph:arrow-down"
                label="Move down"
                disabled={busy || !down}
                onClick={() => down && void onOps([down])}
              />
              <IconButton
                icon={child.hidden ? 'i-ph:eye-slash' : 'i-ph:eye'}
                label={child.hidden ? 'Show block' : 'Hide block'}
                disabled={busy}
                onClick={() => void onOps([{ op: 'update', id: child.id, hidden: !child.hidden }])}
              />
              <IconButton
                icon="i-ph:trash"
                label="Remove block"
                disabled={busy}
                onClick={() => void onOps([{ op: 'remove', id: child.id }])}
              />
            </div>
            {open === child.id && block && (
              <div className="border-t border-hairline px-3 py-3">
                <SettingsEditor
                  block={block}
                  settings={child.settings}
                  issues={[]}
                  busy={busy}
                  references={references}
                  onSave={(settings) => onOps([{ op: 'update', id: child.id, settings, replaceSettings: true }])}
                />
              </div>
            )}
          </div>
        );
      })}
      {options.length > 0 && !full && (
        <div className="flex items-center gap-2">
          <Select value={pick} onChange={setPick} label="Block to add" className="min-w-0 flex-1">
            <option value="">Add a block…</option>
            {options.map((o) => (
              <option key={o.type} value={o.type}>
                {o.schema.title}
              </option>
            ))}
          </Select>
          <button
            type="button"
            disabled={busy || !pick}
            onClick={async () => {
              if (await onOps([{ op: 'add_block', parentId: section.id, type: pick }])) {
                setPick('');
              }
            }}
            className={BTN_SECONDARY}
          >
            Add
          </button>
        </div>
      )}
      {full && (
        <p className="text-[11px] text-muted">This section holds at most {sectionBlock.schema.maxBlocks} blocks.</p>
      )}
    </div>
  );
}

function EmbedCard({
  row,
  busy,
  references,
  onOps,
}: {
  row: ReturnType<typeof embedRows>[number];
  busy: boolean;
  references: ReferenceLoader;
  onOps: (ops: LayoutOp[]) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const { block } = row;
  const hasSettings = block.schema.settings.length > 0;

  return (
    <article className={classNames(CARD, 'overflow-hidden')}>
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <div className="text-[12px] font-medium text-primary">{block.schema.title}</div>
          {block.schema.description && (
            <p className="mt-0.5 text-[11px] leading-relaxed text-muted">{block.schema.description}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {hasSettings && row.enabled && (
            <IconButton icon="i-ph:sliders-horizontal" label="Embed settings" onClick={() => setOpen((v) => !v)} />
          )}
          <Switch
            checked={row.enabled}
            disabled={busy}
            label={row.enabled ? 'On' : 'Off'}
            onChange={(enabled) => void onOps([{ op: 'set_embed', type: block.type, enabled }])}
          />
        </div>
      </div>
      {open && row.enabled && (
        <div className="border-t border-hairline px-4 py-4">
          <SettingsEditor
            block={block}
            settings={row.settings}
            issues={[]}
            busy={busy}
            references={references}
            onSave={(settings) => onOps([{ op: 'set_embed', type: block.type, enabled: true, settings }])}
          />
        </div>
      )}
    </article>
  );
}
